import type { ReploHostingType } from "./OnboardingChooseBuilderType";

import React, { useState } from "react";

import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
} from "@v2/contexts/WorkspaceDashboardContext";
import { useOnboardingCreateProject } from "@v2/features/onboarding/hooks/useOnboardingCreateProject";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import useInstallShopify from "@v2/hooks/useInstallShopify";
import { projectUtils } from "@v2/trpc/routes/project";
import { workspaceRouter, workspaceUtils } from "@v2/trpc/routes/workspace";
import { useNavigate, useSearchParams } from "react-router-dom";

import OnboardingChooseBuilderType from "./OnboardingChooseBuilderType";
import OnboardingFooter from "./OnboardingFooter";
import OnboardingShopInfo from "./OnboardingShopInfo";

type OnboardingStep = "shop-info" | "setup-shop";

const OnboardingCreateShop: React.FC = () => {
  const { user } = useCurrentUser();
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [currentStep, setCurrentStep] = useState<OnboardingStep>("shop-info");

  const [isShopifySelected, setIsShopifySelected] = useState<boolean | null>(
    null,
  );
  const [shopName, setShopName] = useState("");

  const [selectedHostingType, setSelectedHostingType] =
    useState<ReploHostingType | null>(null);

  const [isIntegrateShopifyButtonLoading, setIsIntegrateShopifyButtonLoading] =
    useState<boolean>(false);
  const [isStartBuildingButtonLoading, setIsStartBuildingButtonLoading] =
    useState<boolean>(false);

  const isShopifyPaymentProcessor = workspace?.paymentProcessor === "shopify";
  const isBusinessContext =
    user?.profileType === "business" || user?.profileType === "other";

  const { createProject, logOnboardingStepCompleted } =
    useOnboardingCreateProject();

  const installShopify = useInstallShopify();

  const { mutateAsync: createWorkspace } = workspaceRouter.create.useMutation();

  const pendingAppInstallationId = searchParams.get("pendingAppInstallationId");
  const shopifyIntegrationId = searchParams.get("shopifyIntegrationId");

  const isShopInfoValid = shopName !== "" && isShopifySelected !== null;
  const isSetupShopValid = selectedHostingType !== null;

  const handleShopInfoSubmit = async () => {
    if (!isShopInfoValid || !user) {
      return;
    }
    setIsIntegrateShopifyButtonLoading(true);

    let effectiveWorkspaceId = workspaceId;

    if (!effectiveWorkspaceId) {
      // NOTE (Cole 2025-11-12): We always set the preferred payment processor to stripe for these workspaces.
      // I was told that these workspace are created to cover the edge case of a user being invited to a shop and then later removed from it.
      // So we would need to create them their own shop and workspace at that point in order to use Replo.
      // In that case there's no reason we would ever want to use Shopify as the payment processor.
      const workspace = await createWorkspace({
        name: `${user.firstName}'s Workspace`,
        paymentProcessor: "stripe",
        eventOrigin: "onboarding",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      effectiveWorkspaceId = workspace.workspace.id;
      void workspaceUtils.getUserWorkspacesList.invalidate();
      void projectUtils.listGroupedByWorkspaceForUser.invalidate();
    }

    logOnboardingStepCompleted({
      userId: user.email,
      stepId: "business-details",
      stepType: "business-details",
      stepData: {
        shopName,
      },
    });

    if (isShopifySelected) {
      setIsIntegrateShopifyButtonLoading(false);
      setCurrentStep("setup-shop");
      return;
    }

    const result = await createProject({
      shopName,
      workspaceId: effectiveWorkspaceId,
      hostingType: isShopifyPaymentProcessor ? "shopify" : "replo",
      userEmail: user.email,
      workspaceName: `${shopName}'s First Workspace`,
    });

    if (!isShopifyPaymentProcessor) {
      navigate(`/shops/${result.project?.id}/home`);
    }
    if (!pendingAppInstallationId && !shopifyIntegrationId) {
      navigate(`/shops/${result.project?.id}/home`);
    }

    setIsIntegrateShopifyButtonLoading(false);
  };

  const handleSetupShopSubmit = async (
    shouldConnectShopify: boolean = true,
  ) => {
    if (!isSetupShopValid || !user || !workspaceId || !shopName) {
      return;
    }

    if (shouldConnectShopify) {
      setIsIntegrateShopifyButtonLoading(true);
    } else {
      setIsStartBuildingButtonLoading(true);
    }

    const result = await createProject({
      shopName,
      workspaceId,
      hostingType: selectedHostingType,
      userEmail: user.email,
      workspaceName: `${shopName}'s Workspace`,
      shouldInvalidateUser: true,
    });

    logOnboardingStepCompleted({
      userId: user.email,
      stepId: "connect-store",
      stepType: "connect-store",
      stepData: {
        selectedHostingType,
        shopName,
      },
    });

    if (shouldConnectShopify) {
      installShopify({
        cookie: {
          type: "onboarding",
          workspaceId: result.project.ownerWorkspaceId,
          projectId: result.project.id,
          hostingType: selectedHostingType,
        },
      });
    } else {
      // NOTE (Berkley, 2025-10-27): Use window.location for hard navigation to bypass React Router's redirect logic
      // The redirect logic in RootRouteLayout would send us back to onboarding if it sees
      // stale user data (without the new project). A hard navigation forces a fresh page load
      // where the user data will be fetched with the new project already in the database.
      const redirectPath =
        selectedHostingType === "replo"
          ? `/shops/${result.project.id}/home`
          : `/editor/${result.project.id}/new`;

      window.location.href = redirectPath;
    }

    setIsIntegrateShopifyButtonLoading(false);
    setIsStartBuildingButtonLoading(false);
  };

  const handleBack = () => {
    if (currentStep === "setup-shop") {
      setCurrentStep("shop-info");
    }
  };

  const handleNext = async () => {
    if (currentStep === "shop-info") {
      await handleShopInfoSubmit();
    } else {
      await handleSetupShopSubmit();
    }
  };

  const isNextDisabled = () => {
    if (currentStep === "shop-info") {
      return (
        isIntegrateShopifyButtonLoading ||
        isStartBuildingButtonLoading ||
        !isShopInfoValid
      );
    } else {
      return (
        isIntegrateShopifyButtonLoading ||
        isStartBuildingButtonLoading ||
        !isSetupShopValid
      );
    }
  };

  const showBackButton = currentStep === "setup-shop";

  const titleText = (() => {
    if (currentStep === "setup-shop") {
      return "How do you want to use Replo?";
    }
    return "Create your first project";
  })();

  return (
    <div className="h-screen w-full flex flex-col">
      <div className="flex-1 overflow-y-auto flex flex-col items-center md:py-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <div className="md:pt-0 my-6 flex flex-col gap-1 text-center md:mb-12 md:gap-3 px-5">
          <h1 className="header-h3 text-2xl md:text-3xl">{titleText}</h1>
          {currentStep === "shop-info" && (
            <p className="text-muted header-h3 text-base font-normal">
              Replo projects are the hub for building and managing your
              business. Create as many as you need.
            </p>
          )}
        </div>
        <div className="lg:w-[1000px] w-full pb-40 md:pb-0 px-5 lg:px-0">
          {currentStep === "shop-info" ? (
            <OnboardingShopInfo
              shopName={shopName}
              setShopName={setShopName}
              isBusinessContext={isBusinessContext}
              isShopifySelected={isShopifySelected}
              onShopifySelectedChange={setIsShopifySelected}
            />
          ) : (
            <div className="flex-1 flex items-start justify-center w-full">
              <div className="w-full">
                <OnboardingChooseBuilderType
                  selectedHostingType={selectedHostingType}
                  onSelectHostingType={setSelectedHostingType}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="absolute right-0 bottom-0 left-0 z-20 border-t md:border-t-0 border-border border-[0.5px]">
        <OnboardingFooter
          onNext={() => void handleNext()}
          onBack={showBackButton ? handleBack : undefined}
          className="w-full lg:max-w-[1000px] px-1 md:px-0"
          isNextLoading={isIntegrateShopifyButtonLoading}
          isNextDisabled={isNextDisabled()}
          showBackButton={showBackButton}
          nextButtonText={
            currentStep === "setup-shop" ? "Connect Shopify" : undefined
          }
          showSecondaryCTA={currentStep === "setup-shop"}
          onSecondaryCTAClick={() => void handleSetupShopSubmit(false)}
          isSecondaryCTADisabled={isNextDisabled()}
          isSecondaryCTALoading={isStartBuildingButtonLoading}
          backButtonVariant={
            currentStep === "setup-shop" ? "tertiary" : "secondary"
          }
        />
      </div>
    </div>
  );
};

export default OnboardingCreateShop;
